import React, { useState, useRef, useEffect } from 'react';
import { Check, Plus, Trash2, GripVertical } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TaskChecklistItem } from '../../lib/types';
import { TaskChecklistActions, ChecklistDraftItem } from './TaskChecklistActions';

interface TaskChecklistProps {
  checklist: TaskChecklistItem[];
  taskId: string;
  onToggleItem: (checklistId: string, isCompleted: boolean) => Promise<void>;
  onCreateItem: (taskId: string, title: string, description?: string, sortOrder?: number) => Promise<any>;
  onUpdateItem: (checklistId: string, updates: any) => Promise<void>;
  onDeleteItem: (checklistId: string) => Promise<void>;
  isReadOnly?: boolean;
  hideHeader?: boolean;
  // Sorgenti alternative (copia da un'altra task, genera con AI): servono titolo e
  // descrizione della task. Se sourceContext manca, le azioni non compaiono.
  sourceContext?: { title: string; description?: string; projectName?: string };
}

interface SortableItemProps {
  item: TaskChecklistItem;
  isEditing: boolean;
  editTitle: string;
  setEditTitle: (title: string) => void;
  onEdit: (item: TaskChecklistItem) => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onToggle: (id: string, completed: boolean) => void;
  onDelete: (id: string) => void;
  isReadOnly: boolean;
  editInputRef: React.RefObject<HTMLInputElement>;
}

function SortableChecklistItem({
  item,
  isEditing,
  editTitle,
  setEditTitle,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onToggle,
  onDelete,
  isReadOnly,
  editInputRef,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: isReadOnly });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isEditing) {
    return (
      <div ref={setNodeRef} style={style} className="flex items-center py-1 px-1">
        <div className="w-4 h-4 mr-2 flex-shrink-0" />
        <input
          ref={editInputRef}
          type="text"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onSaveEdit();
            if (e.key === 'Escape') onCancelEdit();
          }}
          onBlur={onSaveEdit}
          className="flex-1 text-sm bg-transparent text-[#37352F] dark:text-gray-100 outline-none border-b border-[#0f6cbd] py-0.5"
          placeholder="Nome elemento"
        />
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center py-1 px-1 rounded hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-gray-800/50 transition-colors group"
    >
      {/* Drag handle */}
      {!isReadOnly && (
        <div
          {...attributes}
          {...listeners}
          className="w-4 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-3 w-3 text-[#C4C4C4] dark:text-gray-600" />
        </div>
      )}
      {isReadOnly && <div className="w-4 flex-shrink-0" />}

      {/* Checkbox */}
      <button
        onClick={async () => {
          if (isReadOnly) return;
          try {
            await onToggle(item.id, !item.is_completed);
          } catch (error) {
            console.error('Error toggling checklist item:', error);
          }
        }}
        disabled={isReadOnly}
        className={`w-4 h-4 mr-2 flex-shrink-0 rounded border transition-all ${
          item.is_completed
            ? 'bg-[#35A76C] border-[#35A76C] dark:bg-green-500 dark:border-green-500'
            : 'border-[#C4C4C4] dark:border-gray-500 hover:border-[#787774] dark:hover:border-gray-400'
        } ${isReadOnly ? 'cursor-default' : 'cursor-pointer'} flex items-center justify-center`}
      >
        {item.is_completed && (
          <Check className="h-3 w-3 text-white" strokeWidth={3} />
        )}
      </button>

      {/* Title */}
      <span
        onClick={() => !isReadOnly && onEdit(item)}
        className={`flex-1 text-sm cursor-text ${
          item.is_completed
            ? 'text-[#9B9A97] dark:text-gray-500 line-through'
            : 'text-[#37352F] dark:text-gray-100'
        }`}
      >
        {item.title}
        {item.is_required && (
          <span className="ml-1 text-[#EB5757] text-xs">*</span>
        )}
      </span>

      {/* Delete - only on hover */}
      {!isReadOnly && (
        <button
          onClick={() => onDelete(item.id)}
          className="p-1 opacity-0 group-hover:opacity-100 text-[#9B9A97] hover:text-[#EB5757] dark:text-gray-500 dark:hover:text-red-400 transition-all"
          title="Elimina"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function TaskChecklist({
  checklist,
  taskId,
  onToggleItem,
  onCreateItem,
  onUpdateItem,
  onDeleteItem,
  isReadOnly = false,
  hideHeader = false,
  sourceContext
}: TaskChecklistProps) {
  const [newItemTitle, setNewItemTitle] = useState('');
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [items, setItems] = useState<TaskChecklistItem[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  // Sync items with checklist prop
  useEffect(() => {
    const sorted = [...checklist].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setItems(sorted);
  }, [checklist]);

  const completedCount = items.filter(item => item.is_completed).length;
  const totalCount = items.length;
  const completionPercentage = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Focus edit input when editing
  useEffect(() => {
    if (editingItemId && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingItemId]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      // Update sort_order for all affected items
      try {
        await Promise.all(
          newItems.map((item, index) =>
            onUpdateItem(item.id, { sort_order: index })
          )
        );
      } catch (error) {
        console.error('Error updating sort order:', error);
        // Revert on error
        setItems([...checklist].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      }
    }
  };

  const handleCreateItem = async () => {
    if (!newItemTitle.trim()) return;

    try {
      const maxSortOrder = items.length > 0
        ? Math.max(...items.map(item => item.sort_order || 0))
        : -1;

      await onCreateItem(taskId, newItemTitle.trim(), undefined, maxSortOrder + 1);
      setNewItemTitle('');
      inputRef.current?.focus();
    } catch (error) {
      console.error('Error creating checklist item:', error);
    }
  };

  // Aggiunta in blocco (copia da task / proposta AI): stessa API della singola voce,
  // con is_required impostato subito dopo perché onCreateItem non lo accetta.
  const handleAddItems = async (drafts: ChecklistDraftItem[]) => {
    let nextOrder = items.length > 0 ? Math.max(...items.map(item => item.sort_order || 0)) + 1 : 0;
    for (const draft of drafts) {
      try {
        const created = await onCreateItem(taskId, draft.title, undefined, nextOrder++);
        if (draft.is_required && created?.id) {
          await onUpdateItem(created.id, { is_required: true });
        }
      } catch (error) {
        console.error('Error adding checklist items:', error);
      }
    }
  };

  const handleEditItem = (item: TaskChecklistItem) => {
    setEditingItemId(item.id);
    setEditTitle(item.title);
  };

  const handleSaveEdit = async () => {
    if (!editingItemId || !editTitle.trim()) {
      handleCancelEdit();
      return;
    }

    try {
      await onUpdateItem(editingItemId, { title: editTitle.trim() });
      setEditingItemId(null);
      setEditTitle('');
    } catch (error) {
      console.error('Error updating checklist item:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditTitle('');
  };

  if (checklist.length === 0 && isReadOnly) {
    return null;
  }

  return (
    <div className="space-y-0.5">
      {/* Header with progress - hidden when inside accordion */}
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-[#37352F] dark:text-gray-100 flex items-center">
            Checklist
            <span className="ml-2 text-xs text-[#787774] dark:text-gray-400">
              {completedCount}/{totalCount}
            </span>
          </h4>
          {totalCount > 0 && (
            <div className="flex items-center space-x-2">
              <div className="w-16 h-1 bg-[#E3E2E0] dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#35A76C] dark:bg-green-500 transition-all duration-300"
                  style={{ width: `${completionPercentage}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Checklist items with drag and drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div>
            {items.map((item) => (
              <SortableChecklistItem
                key={item.id}
                item={item}
                isEditing={editingItemId === item.id}
                editTitle={editTitle}
                setEditTitle={setEditTitle}
                onEdit={handleEditItem}
                onSaveEdit={handleSaveEdit}
                onCancelEdit={handleCancelEdit}
                onToggle={onToggleItem}
                onDelete={onDeleteItem}
                isReadOnly={isReadOnly}
                editInputRef={editInputRef}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Add new item - Notion inline style */}
      {!isReadOnly && (
        <div className="flex items-center py-1 px-1 group">
          <div className="w-4 flex-shrink-0" />
          <div className={`w-4 h-4 mr-2 flex-shrink-0 rounded border border-dashed transition-colors ${
            newItemTitle ? 'border-[#787774] dark:border-gray-400' : 'border-[#C4C4C4] dark:border-gray-600'
          } flex items-center justify-center`}>
            <Plus className={`h-2.5 w-2.5 transition-colors ${
              newItemTitle ? 'text-[#787774] dark:text-gray-400' : 'text-[#C4C4C4] dark:text-gray-600'
            }`} />
          </div>
          <input
            ref={inputRef}
            type="text"
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && newItemTitle.trim()) {
                handleCreateItem();
              }
            }}
            className="flex-1 text-sm bg-transparent text-[#37352F] dark:text-gray-100 placeholder:text-[#9B9A97] dark:placeholder:text-gray-500 outline-none py-0.5"
            placeholder="Aggiungi elemento..."
          />
        </div>
      )}

      {!isReadOnly && sourceContext && (
        <TaskChecklistActions
          taskId={taskId}
          taskTitle={sourceContext.title}
          taskDescription={sourceContext.description}
          projectName={sourceContext.projectName}
          existingTitles={items.map(item => item.title)}
          onAddItems={handleAddItems}
        />
      )}
    </div>
  );
}
